import { useTranslation } from 'react-i18next';

import { useAuthContext } from '@myphx-engineering/auth/client';
import {
    geolocationDistance,
    useGeolocation,
} from '@myphx-engineering/location-utils';
import {
    Flex,
    IconBolt,
    Modal,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';
import {
    HighlightPharmacyCard,
    PharmacyCard,
    type Pricing,
} from '@phx/myphx-lib';

import { getFragment, getFragments } from '../../../graphql';
import {
    type CashOfferInfoFragment,
    type InsuranceOfferInfoFragment,
    type PharmacyProviderInfoFragment,
    type ProviderOffersInfoFragment,
    SuggestedProviderOffersReason,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { getOffersByProviderId } from '../../../util/offers/get-offers-by-provider-id';
import { getPriceableOffers } from '../../../util/offers/get-priceable-offers';
import { getShippingMethodFromOffers } from '../../../util/offers/get-shipping-method';
import { hasPHX502 } from '../../../util/offers/sort';
import { getDeliveryTimeDisplay } from '../../../util/pharmacy/get-delivery-time-display';
import { getTodaysHours } from '../../../util/pharmacy/get-todays-hours';
import { getPrimaryProvider } from '../../../util/provider-offers/provider-utils';

import { MoreProviders } from './MoreProviders';

export const ProviderOffersCard = ({
    providerOffers,
    onSelected,
    suggestedReasons,
    role,
    showContactForPrice = true,
}: {
    providerOffers: ProviderOffersInfoFragment;
    onSelected: (id: string, providerId?: string) => void;
    suggestedReasons?: SuggestedProviderOffersReason[];
    role?: React.AriaRole;
    showContactForPrice?: boolean;
}) => {
    const { t } = useTranslation();
    const { costPlusDrugsEnabled } = useFlags();
    const { isAuthenticated } = useAuthContext();
    const { preferences } = usePatientContext();
    const { favoritePharmacies } = preferences;
    const { checkIsFavorite, toggleFavorite } = favoritePharmacies;
    const [opened, { open, close }] = useDisclosure(false);
    const { geolocation } = useGeolocation();
    const primaryPharmacy = getPrimaryProvider(providerOffers, geolocation);
    if (!primaryPharmacy) {
        throw new Error('No pharmacy for offer');
    }

    const pharmacyDoesNotCarryMedication = hasPHX502(providerOffers);

    const description: string[] = [];
    let additionalInfo;

    if (primaryPharmacy.__typename === 'PharmacyProvider') {
        description.push(
            t('common.distance.distanceWithUnit', {
                miles: Math.max(
                    0.1,
                    geolocation
                        ? geolocationDistance(geolocation, primaryPharmacy)
                        : providerOffers.distance
                ).toFixed(1),
            }),
            primaryPharmacy.description ?? ''
        );

        additionalInfo = getTodaysHours(primaryPharmacy);
    } else if (primaryPharmacy.__typename === 'MailOrderProvider') {
        if (!pharmacyDoesNotCarryMedication) {
            if (costPlusDrugsEnabled) {
                // Get all offers for this provider
                const offers = getOffersByProviderId(
                    getPriceableOffers(providerOffers),
                    primaryPharmacy.id
                ) as Array<CashOfferInfoFragment | InsuranceOfferInfoFragment>;

                // Extract shipping method from offers
                const shippingMethod = getShippingMethodFromOffers(offers);

                // Get delivery time for the specific shipping method
                const deliveryTime = getDeliveryTimeDisplay(
                    primaryPharmacy,
                    shippingMethod
                );

                if (deliveryTime) {
                    description.push(deliveryTime);
                }
            } else if (primaryPharmacy.deliveryTimeDisplay) {
                description.push(primaryPharmacy.deliveryTimeDisplay);
            }
        }
    }

    const offers = getOffersByProviderId(
        getPriceableOffers(providerOffers),
        primaryPharmacy.id
    ) as Array<CashOfferInfoFragment | InsuranceOfferInfoFragment>;
    const pricing = offers.map((offer) => offer.pricingDetail as Pricing);

    let morePharmacies: PharmacyProviderInfoFragment[] = [];
    if (providerOffers.provider.__typename === 'ChainProvider') {
        const chainProvider = getFragment(providerOffers.provider);
        if (chainProvider?.pharmacies?.length) {
            morePharmacies = getFragments(chainProvider.pharmacies).filter(
                (pharmacy) => pharmacy.id !== primaryPharmacy.id
            );
        }
    }
    // Temporary until VELA integration
    const instantRouting = false;

    const toggleFavoriteIcon = isAuthenticated
        ? () => toggleFavorite(primaryPharmacy.id)
        : undefined;

    const shouldShowFooter = morePharmacies.length || instantRouting;

    const primaryName =
        providerOffers.provider.__typename === 'ChainProvider'
            ? getFragment(providerOffers.provider)?.chainName
            : primaryPharmacy.name;

    const footer = shouldShowFooter ? (
        <Flex justify="space-between">
            <MoreProviders
                primaryName={primaryName}
                moreProviders={morePharmacies}
                onSelected={(id: string) => onSelected(providerOffers.id, id)}
            />
            {instantRouting ? (
                <Flex style={{ marginLeft: 'auto ' }} gap="xxxs" align="center">
                    <IconBolt
                        height={19}
                        variant="rounded"
                        filled
                        color="info"
                    />
                    <Subtitle2 color="info">
                        {t('controlledSubstance.modal.instantRouting')}
                    </Subtitle2>
                </Flex>
            ) : null}
        </Flex>
    ) : null;

    if (suggestedReasons?.includes(SuggestedProviderOffersReason.Cheapest)) {
        const fulfillmentType =
            providerOffers.provider.__typename === 'MailOrderProvider'
                ? t('offerPharmacyCard.delivery')
                : t('offerPharmacyCard.pickup');
        const heading = `${t('offerPharmacyCard.greatValue.heading')} (${fulfillmentType})`;

        return (
            <>
                <HighlightPharmacyCard
                    role={role}
                    title={primaryPharmacy.name}
                    favorite={checkIsFavorite(primaryPharmacy.id)}
                    toggleFavorite={toggleFavoriteIcon}
                    description={description}
                    pricing={pricing}
                    onClick={() => onSelected(providerOffers.id)}
                    variant="GreatValue"
                    header={heading}
                    onIconClick={open}
                    footer={footer}
                    additionalInfo={additionalInfo}
                />
                <Modal
                    title={t('offerPharmacyCard.greatValue.modal.title')}
                    onClose={close}
                    opened={opened}
                >
                    {t('offerPharmacyCard.greatValue.modal.content')}
                </Modal>
            </>
        );
    }

    const subTitle = pharmacyDoesNotCarryMedication
        ? t('offerPharmacyCard.pharmacyDoesNotCarry')
        : pricing.length || !showContactForPrice
          ? undefined
          : t('offerPharmacyCard.contactForPrice');

    return (
        <PharmacyCard
            role={role}
            title={primaryPharmacy.name}
            favorite={checkIsFavorite(primaryPharmacy.id)}
            toggleFavorite={toggleFavoriteIcon}
            description={description}
            pricing={pricing}
            onClick={() => onSelected(providerOffers.id)}
            footer={footer}
            subtitle={subTitle}
            additionalInfo={additionalInfo}
        />
    );
};
